import { memo, useMemo } from 'react';
import { Alert, Badge, Button, ProgressBar } from 'react-bootstrap';

import classNames from 'classnames';

import Icon from '@/components/Icon';

import { analyzeGeoQuestion, appendGeoQuestionTemplate } from './utils';

import './index.scss';

interface Props {
  title: string;
  content: string;
  tagCount: number;
  className?: string;
  onChangeContent?: (content: string) => void;
}

const scoreVariant = (score: number) => {
  if (score >= 85) {
    return 'success';
  }
  if (score >= 60) {
    return 'warning';
  }
  return 'danger';
};

const QuestionGeoAssistant = ({
  title,
  content,
  tagCount,
  className,
  onChangeContent,
}: Props) => {
  const analysis = useMemo(
    () => analyzeGeoQuestion(title, content, tagCount),
    [title, content, tagCount],
  );
  const failedChecks = analysis.checks.filter((check) => !check.passed);
  const passedChecks = analysis.checks.filter((check) => check.passed);
  const variant = scoreVariant(analysis.score);

  const applyTemplate = () => {
    onChangeContent?.(appendGeoQuestionTemplate(content));
  };

  return (
    <section className={classNames('geo-question-assistant', className)}>
      <div className="geo-question-head">
        <div>
          <div className="geo-question-title">
            <Icon type="bi" name="stars" className="me-2" />
            GEO 提问助手
            <Badge bg={variant} className="ms-2">
              {analysis.score}
            </Badge>
          </div>
          <p className="geo-question-desc">
            把环境、版本、现象、复现步骤和期望结果写清楚，回答者更容易给出可验证答案，AI
            采集器也更容易识别主题和证据。
          </p>
        </div>
        {onChangeContent && (
          <Button
            type="button"
            size="sm"
            variant="outline-primary"
            onClick={applyTemplate}>
            <Icon type="bi" name="file-earmark-plus" className="me-1" />
            插入提问结构
          </Button>
        )}
      </div>

      <ProgressBar
        now={analysis.score}
        variant={variant}
        className="geo-question-score"
      />

      <div className="geo-question-checks">
        {[...failedChecks, ...passedChecks].map((check) => (
          <div
            key={check.id}
            className={classNames(
              'geo-question-check',
              check.passed ? 'is-passed' : 'is-missing',
            )}>
            <Icon
              type="bi"
              name={check.passed ? 'check-circle-fill' : 'circle'}
              className="geo-question-check-icon"
            />
            <div>
              <strong>{check.label}</strong>
              <span>{check.hint}</span>
            </div>
          </div>
        ))}
      </div>

      {analysis.missingHints.length > 0 && (
        <Alert variant="light" className="geo-question-tips">
          建议先补齐上面未满足的项目，再发布问题。
        </Alert>
      )}
    </section>
  );
};

export default memo(QuestionGeoAssistant);
